import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  billingCyclesService,
  BillingCycleCreateData,
  BillingCycleUpdateData,
  BillingCycle
} from '@/services/billing/billing-cycles.service';

// Query Keys
export const billingCyclesKeys = {
  all: ['billing-cycles'] as const,
  lists: () => [...billingCyclesKeys.all, 'list'] as const,
  list: (productId: string) => [...billingCyclesKeys.lists(), productId] as const,
  details: () => [...billingCyclesKeys.all, 'detail'] as const,
  detail: (productId: string, cycleId: string) => [...billingCyclesKeys.details(), productId, cycleId] as const,
};

export function useBillingCycles(productId: string) {
  return useQuery<BillingCycle[], Error>({
    queryKey: billingCyclesKeys.list(productId),
    queryFn: () => billingCyclesService.getBillingCycles(productId),
    enabled: !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      if ('status' in (error as Error) && (error as Error & { status?: number }).status === 404) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

export function useBillingCycle(productId: string, cycleId: string) {
  return useQuery<BillingCycle | null, Error>({
    queryKey: billingCyclesKeys.detail(productId, cycleId),
    queryFn: () => billingCyclesService.getBillingCycle(productId, cycleId),
    enabled: !!productId && !!cycleId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, error) => {
      if ('status' in (error as Error) && (error as Error & { status?: number }).status === 404) {
        return false;
      }
      return failureCount < 3;
    }
  });
}

export function useCreateBillingCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, data }: { productId: string; data: BillingCycleCreateData }) =>
      billingCyclesService.createBillingCycle(productId, data),
    onSuccess: (newCycle, { productId }) => {
      // Invalidate billing cycles list
      queryClient.invalidateQueries({ queryKey: billingCyclesKeys.list(productId) });
      
      // Optimistically add to cache
      queryClient.setQueryData(billingCyclesKeys.detail(productId, newCycle.id), newCycle);
      
      // Invalidate related plan pricing queries since cycles are needed for pricing
      queryClient.invalidateQueries({ queryKey: ['plan-pricing', productId] });
    },
  });
}

export function useUpdateBillingCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ 
      productId, 
      cycleId, 
      data 
    }: { 
      productId: string; 
      cycleId: string; 
      data: BillingCycleUpdateData 
    }) =>
      billingCyclesService.updateBillingCycle(productId, cycleId, data),
    onSuccess: (updatedCycle, { productId, cycleId }) => {
      // Update the specific cycle in cache
      queryClient.setQueryData(billingCyclesKeys.detail(productId, cycleId), updatedCycle);
      
      // Invalidate cycles list to refresh summaries
      queryClient.invalidateQueries({ queryKey: billingCyclesKeys.list(productId) });
      
      // Invalidate plan pricing since cycle changes might affect pricing
      queryClient.invalidateQueries({ queryKey: ['plan-pricing', productId] });
      
      // Invalidate credit management since shortest cycle affects credit reset
      queryClient.invalidateQueries({ queryKey: ['credit-management', productId] });
    },
  });
}

export function useDeleteBillingCycle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ productId, cycleId }: { productId: string; cycleId: string }) =>
      billingCyclesService.deleteBillingCycle(productId, cycleId),
    onSuccess: (_, { productId, cycleId }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: billingCyclesKeys.detail(productId, cycleId) });
      
      // Invalidate list to refresh
      queryClient.invalidateQueries({ queryKey: billingCyclesKeys.list(productId) });
      
      // Invalidate plan pricing since pricing depends on available cycles
      queryClient.invalidateQueries({ queryKey: ['plan-pricing', productId] });
      
      // Invalidate credit management since shortest cycle affects credit reset
      queryClient.invalidateQueries({ queryKey: ['credit-management', productId] });
    },
  });
}

// Prefetch Hook for hover/preview scenarios
export function usePrefetchBillingCycle() {
  const queryClient = useQueryClient();

  return (productId: string, cycleId: string) => {
    queryClient.prefetchQuery({
      queryKey: billingCyclesKeys.detail(productId, cycleId),
      queryFn: () => billingCyclesService.getBillingCycle(productId, cycleId),
      staleTime: 5 * 60 * 1000,
    });
  };
}

// Utility hook to get the shortest billing cycle (used for credit management)
export function useShortestBillingCycle(productId: string) {
  const { data: cycles, ...rest } = useBillingCycles(productId);
  
  const shortestCycle = cycles && cycles.length > 0 
    ? cycles.reduce((shortest, current) => 
        current.months < shortest.months ? current : shortest
      )
    : null;

  return {
    ...rest,
    data: shortestCycle,
    cycles // Also return all cycles in case needed
  };
}
